import { type DragEvent, type ReactElement } from 'react'
import { cn } from '@util'
import {
  isCompanyId,
  isEmpty,
  isRoleId,
  isTeamId,
  isUserId,
  pluralize,
  type DashboardDataWithEditable,
} from '@terros/common'
import { useNavigate } from '@tanstack/react-router'
import { Badge, Group, Stack, Text, UnstyledButton } from '@mantine/core'
import { SizePrimitives, useTranslation } from '@front/model'
import { MaterialSymbolIcon } from '@components'
import { DashboardRowMenu } from './DashboardRowMenu'

type DashboardItemProps = {
  dashboard: DashboardDataWithEditable
  /** True when the dashboard came from a broader scope rather than this one. */
  isShared: boolean
  deleting?: boolean
  dragging?: boolean
  reordering: boolean
  onDelete: (dashboard: DashboardDataWithEditable) => void
  onDuplicate: (dashboard: DashboardDataWithEditable) => void
  onDragEnd: () => void
  onDragOver: (event: DragEvent<HTMLDivElement>) => void
  onDragStart: () => void
  onDrop: (event: DragEvent<HTMLDivElement>) => void
}

export const DashboardItem = ({
  dashboard,
  isShared,
  deleting = false,
  dragging = false,
  reordering,
  onDelete,
  onDuplicate,
  onDragEnd,
  onDragOver,
  onDragStart,
  onDrop,
}: DashboardItemProps): ReactElement => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const dashboardName = dashboard.name?.trim()
  const scopeLabel = t(toDashboardScopeLabel(dashboard.scopeId))
  const isReadOnly = dashboard.editable !== true
  const canDrag = reordering && dashboard.editable === true && dashboard.home !== true

  return (
    <div
      className={cn('border-default-border p-2xl w-full rounded-xl border transition-colors', {
        'opacity-50': dragging || isReadOnly,
      })}
      draggable={canDrag}
      onDragEnd={canDrag ? onDragEnd : undefined}
      onDragOver={onDragOver}
      onDragStart={canDrag ? onDragStart : undefined}
      onDrop={onDrop}
    >
      <Group gap='sm' wrap='nowrap'>
        <UnstyledButton
          className='min-w-0 flex-1'
          disabled={deleting || isReadOnly}
          onClick={
            reordering || isReadOnly ? undefined : () => navigate({ to: `/dashboard/${dashboard.dashboardId}/edit` })
          }
        >
          <Group gap='sm' justify='space-between' wrap='nowrap'>
            <Group gap='sm' wrap='nowrap'>
              {canDrag && (
                <MaterialSymbolIcon className='cursor-grab' icon='drag_indicator' size={SizePrimitives['4']} />
              )}
              <Stack className='text-start' gap={SizePrimitives[0]}>
                <Text fw={500}>{isEmpty(dashboardName) ? t('Dashboard') : dashboardName}</Text>
                <Text c='dimmed' size='sm'>
                  {dashboard.widgets.length} {pluralize(dashboard.widgets.length, 'widget')} · {scopeLabel}
                </Text>
              </Stack>
            </Group>
            <Group gap='xs' wrap='nowrap'>
              {isShared && (
                <Badge color='neutral' size='sm' variant='light'>
                  {t('Shared')}
                </Badge>
              )}
              {isReadOnly ? (
                <MaterialSymbolIcon icon='lock' size={SizePrimitives['4']} />
              ) : (
                <MaterialSymbolIcon icon='chevron_right' size={SizePrimitives['5']} />
              )}
            </Group>
          </Group>
        </UnstyledButton>
        {!reordering && (
          <DashboardRowMenu dashboard={dashboard} isBusy={deleting} onDelete={onDelete} onDuplicate={onDuplicate} />
        )}
      </Group>
    </div>
  )
}

function toDashboardScopeLabel(scopeId: DashboardDataWithEditable['scopeId']): string {
  if (isCompanyId(scopeId)) return 'Company'
  if (isTeamId(scopeId)) return 'Team'
  if (isUserId(scopeId)) return 'Personal'
  if (isRoleId(scopeId)) return 'Role'
  return 'Dashboard'
}
